import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarRange } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  ErrorState,
  Skeleton,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatCurrency } from '@/lib/utils';
import { OUTBOUND_KEY, processesApi } from './processes-api';

function monthKey(offset: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * החשבון החודשי (תהליך "ניהול עבודה"): החודש הנוכחי מתעדכן לבד, והחודש
 * שהסתיים ממתין לאישור — רק האישור מפיק חשבון עסקה ושולח אותו.
 */
export function MonthlyBilling() {
  const { t } = useTranslation(['processes', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [period, setPeriod] = useState(() => monthKey(-1));
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  const overview = useQuery({
    queryKey: ['billing', 'monthly', period],
    queryFn: ({ signal }) => processesApi.monthly(period, signal),
  });

  const billable = (overview.data?.customers ?? []).filter((c) => c.billableLines > 0);
  const selected = billable.filter((c) => !excluded.has(c.customer.id));

  const approve = useMutation({
    mutationFn: () =>
      processesApi.approveMonthly(
        period,
        selected.map((c) => c.customer.id),
      ),
    onSuccess: (res) => {
      const ok = res.results.filter((r) => r.ok).length;
      const failed = res.results.length - ok;
      if (res.error) toast.error(t('monthly.notIssued'), res.error);
      else toast.success(t('monthly.issued', { count: ok }), failed ? t('monthly.failed', { count: failed }) : undefined);
      setExcluded(new Set());
      void qc.invalidateQueries({ queryKey: ['billing'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: OUTBOUND_KEY });
    },
    onError: (err: unknown) =>
      toast.error(t('monthly.issueFailed'), err instanceof ApiError ? err.message : undefined),
  });

  const total = selected.reduce((sum, c) => sum + Number(c.total), 0);

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <CalendarRange className="size-4 text-accent" aria-hidden />
            {t('monthly.title')}
          </CardTitle>
          <CardDescription>{t('monthly.description')}</CardDescription>
        </div>
        <div className="flex gap-1" role="group" aria-label={t('monthly.monthGroup')}>
          {[
            { key: monthKey(-1), label: t('monthly.lastMonth') },
            { key: monthKey(0), label: t('monthly.thisMonth') },
          ].map((m) => (
            <Button
              key={m.key}
              size="sm"
              variant={period === m.key ? 'secondary' : 'ghost'}
              aria-pressed={period === m.key}
              onClick={() => {
                setPeriod(m.key);
                setExcluded(new Set());
              }}
            >
              {m.label}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {overview.isPending ? (
          <div className="p-4">
            <Skeleton className="h-24" />
          </div>
        ) : overview.isError ? (
          <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
        ) : billable.length === 0 ? (
          <p className="px-5 py-6 text-center text-sm text-fg-muted">
            {t('monthly.empty', { period: overview.data.period.label })}
          </p>
        ) : (
          <>
            <ul className="divide-y divide-border">
              {billable.map((c) => (
                <li key={c.customer.id} className="flex items-center gap-3 px-5 py-3">
                  {!overview.data.isOpen ? (
                    <Checkbox
                      checked={!excluded.has(c.customer.id)}
                      aria-label={t('monthly.includeAria', { name: c.customer.name })}
                      onCheckedChange={(checked) =>
                        setExcluded((prev) => {
                          const next = new Set(prev);
                          if (checked) next.delete(c.customer.id);
                          else next.add(c.customer.id);
                          return next;
                        })
                      }
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-fg">{c.customer.name}</p>
                    <p className="text-xs text-fg-muted">
                      {t('monthly.jobs', { count: c.tasks })} · {t('monthly.lines', { count: c.billableLines })}
                      {!c.customer.email && !c.customer.phone ? ` · ${t('monthly.noContact')}` : ''}
                    </p>
                  </div>
                  <span className="tabular text-sm font-semibold text-fg">
                    {formatCurrency(c.total)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
              {overview.data.isOpen ? (
                <Badge tone="info">{t('monthly.stillOpen')}</Badge>
              ) : (
                <>
                  <span className="text-sm text-fg-muted">
                    {t('monthly.customers', { count: selected.length })} ·{' '}
                    <span className="tabular font-medium text-fg">{formatCurrency(total)}</span>
                  </span>
                  {can('OWNER') ? (
                    <Button
                      loading={approve.isPending}
                      disabled={selected.length === 0}
                      onClick={() => approve.mutate()}
                    >
                      {t('monthly.approve')}
                    </Button>
                  ) : null}
                </>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
